import { BreakpointObserver, MediaMatcher } from "@angular/cdk/layout";
import { Component, OnInit, ViewChild, ViewEncapsulation } from "@angular/core";
import { distinctUntilChanged, filter, Subscription } from "rxjs";
import { MatSidenav, MatSidenavContent } from "@angular/material/sidenav";
import { NgScrollbarModule } from "ngx-scrollbar";
import { HeaderComponent } from "./vertical/header/header.component";
import { AppBreadcrumbComponent } from "./shared/breadcrumb/breadcrumb.component";
import { MaterialModule } from "src/app/material.module";
import { NavigationEnd, Router, RouterModule } from "@angular/router";
import { CommonModule } from "@angular/common";
import { AppNavItemComponent } from "./vertical/sidebar/nav-item/nav-item.component";
import { CoreService } from "@assoanalytics/app/core/services/core.service";
import { NavService } from "@assoanalytics/app/core/services/nav.service";
import { EventService } from "@assoanalytics/app/core/services/event.service";
import { NavItem } from "./vertical/sidebar/nav-item/nav-item";
import { AuthService } from "@assoanalytics/app/core/services/auth.service";
import { AclService } from "@assoanalytics/app/core/acl/acl.service";
import {
  MOBILE_VIEW,
  MONITOR_VIEW,
  TABLET_VIEW,
} from "@assoanalytics/app/core/enum/breakpoint-constants";
import { navItems } from "./vertical/sidebar/sidebar-data";
import { AppSettings } from "@assoanalytics/app/core/models/app-settings";
import { version } from "@assoanalytics/environments/version";

@Component({
  selector: "app-full",
  templateUrl: "./full.component.html",
  imports: [
    NgScrollbarModule,
    HeaderComponent,
    AppBreadcrumbComponent,
    MaterialModule,
    RouterModule,
    CommonModule,
    AppNavItemComponent,
  ],
  styleUrls: [],
  encapsulation: ViewEncapsulation.None,
})
export class FullComponent implements OnInit {
  currentNavItems: NavItem[];
  showInfo = false;
  isDashboardRoute = false;
  @ViewChild("leftsidenav")
  public sidenav: MatSidenav;

  @ViewChild(MatSidenavContent, { static: true }) 
  mainContent: MatSidenavContent;

  //get options from service
  options = this.settings.getOptions();
  navopt = this.navService.showClass;
  private layoutChangesSubscription = Subscription.EMPTY;
  private isMobileScreen = false;
  private isContentWidthFixed = true;
  private isCollapsedWidthFixed = false;
  private htmlElement!: HTMLHtmlElement;

  indexOnExpand: number;

  wrapperHeight: string = "calc(100vh - 70px)";

  version = version;

  get isOver(): boolean {
    return this.isMobileScreen;
  }

  constructor(
    private settings: CoreService,
    private mediaMatcher: MediaMatcher,
    private navService: NavService,
    private breakpointObserver: BreakpointObserver,
    private authService: AuthService,
    private aclService: AclService,
    private router: Router,
  ) {
    // SCROLL TO TOP ALLA NAVIGAZIONE
    this.router.events.subscribe(e => {
      if (e instanceof NavigationEnd) {
        this.mainContent.scrollTo({top:0})
      }
    });

    this.htmlElement = document.querySelector("html")!;
    this.layoutChangesSubscription = this.breakpointObserver
      .observe([MOBILE_VIEW, TABLET_VIEW, MONITOR_VIEW])
      .subscribe((state) => {
        // SidenavOpened must be reset true when layout changes
        this.options.sidenavOpened = true;
        this.isMobileScreen = state.breakpoints[MOBILE_VIEW];
        if (this.options.sidenavCollapsed == false) {
          this.options.sidenavCollapsed = state.breakpoints[TABLET_VIEW];
        }
        this.isContentWidthFixed = state.breakpoints[MONITOR_VIEW];
      });

    // Initialize project theme with options
    this.receiveOptions(this.options);

    EventService.pageWrapperHeight.subscribe((height: string) => {
      setTimeout(() => (this.wrapperHeight = height));
    });
  }

  async ngOnInit(): Promise<void> {
    // Check initial route for setup template pageWrapper class
    this.isDashboardRoute =
      this.router.url === "/dashboard" ||
      this.router.url.startsWith("/dashboard/");

    // Monitor route changes to detect dashboard
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isDashboardRoute =
          event.url === "/dashboard" || event.url.startsWith("/dashboard/");
      });

    this.authService.currentUser
      .subscribe({
        next: (value) => {
          this.currentNavItems = [];

          if (value) {
            // Attendi che AclService sia pronto PRIMA di costruire il menu
            this.aclService.ready$
              .pipe(
                filter((ready) => ready === true),
                // take(1)
                distinctUntilChanged(),
              )
              .subscribe(() => {
                this.currentNavItems = [...navItems[value.role]];

                this.currentNavItems = this.currentNavItems
                  .filter((navItem) =>
                    this.aclService.isModuleEnabled(navItem.module),
                  )
                  .map((navItem) => {
                    const parsedNavItem = { ...navItem };
                    if (parsedNavItem.children) {
                      const visibleMenus = this.aclService.getVisibleMenus(
                        navItem.module,
                      );
                      parsedNavItem.children = parsedNavItem.children.filter(
                        (subNavItem) => {
                          if (
                            !subNavItem.component ||
                            (subNavItem.component &&
                              !visibleMenus[subNavItem.component])
                          ) {
                            return false;
                          }
                          if (subNavItem.roles) {
                            return subNavItem.roles.includes(value.role);
                          }
                          return subNavItem;
                        },
                      );
                    }
                    return parsedNavItem;
                  });
              });
          }
        },
      })
      .unsubscribe();
  }

  ngOnDestroy() {
    this.layoutChangesSubscription.unsubscribe();
  }

  toggleCollapsed() {
    this.isContentWidthFixed = false;
    this.options.sidenavCollapsed = !this.options.sidenavCollapsed;
    this.resetCollapsedState();
  }

  resetCollapsedState(timer = 400) {
    setTimeout(() => this.settings.setOptions(this.options), timer);
  }

  onSidenavClosedStart() {
    this.isContentWidthFixed = false;
  }

  onSidenavOpenedChange(isOpened: boolean) {
    this.isCollapsedWidthFixed = !this.isOver;
    this.options.sidenavOpened = isOpened;
    this.settings.setOptions(this.options);
  }

  receiveOptions(options: AppSettings): void {
    this.options = options;
    this.toggleDarkTheme(options);
  }

  toggleDarkTheme(options: AppSettings) {
    if (options.theme === "dark") {
      this.htmlElement.classList.add("dark-theme");
      this.htmlElement.classList.remove("light-theme");
    } else {
      this.htmlElement.classList.remove("dark-theme");
      this.htmlElement.classList.add("light-theme");
    }
  }

  setIndexItemOnExpand(index: number) {
    if (index === this.indexOnExpand) {
      this.indexOnExpand = -1;
    } else {
      this.indexOnExpand = index;
    }
  }
}
